import { useEffect, useState } from "react"
import { URL_FOR_API } from "../../../constants/constants"
import actionsWithApi from "../../../customHooks/actionsWithApi"
import dateFormatting from "../../../scripts/dateFormatting"
import currencyFormatting from "../../../scripts/currencyFormatting"
import { additionalFundingRequestsType, contractPaymentDetailsType } from "../../../types/contractTypes"
import s from "./ContractInfo.module.scss"
import Preloader from '../../shared/Preloader/Preloader'
import ChildContractsList from "./ChildContractsList/ChildContractsList"
import { Button, createStyles, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, withStyles } from "@material-ui/core"
import Paper from '@material-ui/core/Paper'
import getCookie from "../../../scripts/getCookie"

const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
        },
        body: {
            fontSize: 14,
        },
        btn: {
            display: "block",
            margin: "20px auto"
        },
        date: {
            minWidth: '75px'
        }
    }),
)(TableCell)

const StyledTableRow = withStyles((theme: Theme) =>
    createStyles({
        root: {
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    }),
)(TableRow)

export default function ContractInfo({ data }: any) {
    const currentUserRole = getCookie("currentUserRole")
    const [finalAmount, setFinalAmount] = useState<number | null>(null)
    const [totalDueIsVisible, setTotalDueIsVisible] = useState<boolean>(false)
    const [paymentsSectionVisible, setPaymentsSectionVisible] = useState<boolean>(false)

    const getFinalAmount = async (data: any) => {
        if (data && data.id) {
            try {
                let response = await actionsWithApi("GET", URL_FOR_API + `/api/v1/Contract/${data.id}/calculate`)
                setFinalAmount(response.data.totalAmountOwed)
            }
            catch (error) {
                console.log(error.message)
            }
        }
    }

    const calcTotalDueVisible = (data: any) => {
        if (data && data.contractStatus.length > 0) {
            for (let i = 0; i < data.contractStatus.length; i++) {
                if (data.contractStatus[i].contractsStatus === "CheckSent") setTotalDueIsVisible(true)
            }
        }
    }

    const togglePaymentsSection = () => {
        setPaymentsSectionVisible(!paymentsSectionVisible);
    }

    useEffect(() => {
        getFinalAmount(data)
        calcTotalDueVisible(data)
    }, [data?.id])

    if (data) {
        return (
            <section className={s["ContractInfoWrapper"]}>
                <section className={s["ContractInfo"]}>

                    <div className={s["ContractInfo-Col"]}>
                        <ul className={s["ContractInfo-List"]}>
                            <li className={s["ContractInfo-Item"]}>
                                <span>Requested amount:</span>
                                <span>{data.amountRequested ? data.amountRequested.amount : ""}</span>
                            </li>
                            {data.additionalFundingRequestsDto && data.additionalFundingRequestsDto.length > 0 &&
                                data.additionalFundingRequestsDto
                                    .map((request: additionalFundingRequestsType, index: number) => {
                                        return (
                                            <li className={s["ContractInfo-Item"] + " " + s["ContractInfo-Item__AdditionalRequest"]} key={request.id}>
                                                <span>Additional Request {index + 1}: </span>
                                                <span>{request.amount}</span>
                                            </li>
                                        )
                                    })
                            }
                            <li className={s["ContractInfo-Item"]}>
                                <span>Settlement amount:</span>
                                <span>{data.amountSettlement}</span>
                            </li>
                            <li className={s["ContractInfo-Item"]}>
                                <span>Estimated Settlement amount:</span>
                                <span>{data.estimatedSettlement}</span>
                            </li>
                            <li className={s["ContractInfo-Item"]}>
                                <span>Estimated Additional Funding Needed:</span>
                                <span>{data.estimatedAdditional}</span>
                            </li>
                            <li className={s["ContractInfo-Item"]}>
                                <span>Approved amount:</span>
                                <span>{data.amountApproved ? data.amountApproved.amount : ""}</span>
                            </li>
                            {
                                data.childContracts && data.childContracts.length > 0 &&
                                <ChildContractsList childContracts={data.childContracts} />

                            }
                            <li className={s["ContractInfo-Item"]}>
                                <span>Current Amount Due:</span>
                                <span>{finalAmount ? finalAmount : ""}</span>
                            </li>
                            <li className={s["ContractInfo-Item"]}>
                                <span>Amount owed after closing:</span>
                                <span>{data.owedAfterClosing ? data.owedAfterClosing : ""}</span>
                            </li>
                        </ul>
                    </div>

                    <div className={s["ContractInfo-Col"]}>
                        <ul className={s["ContractInfo-List"]}>
                            {data && data.amountPayments && data.amountPayments.map((item: any, index: number) => {
                                return (
                                    <li className={s["ContractInfo-Item"]} key={index}>
                                        <div>
                                            <span>Payment {index + 1}: </span>
                                            <span>${item.amount}</span>
                                        </div>
                                        <div>
                                            {dateFormatting(item.date)}
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                        {data.amountPayments.length === 0 &&
                            <b>No payments</b>
                        }
                        {totalDueIsVisible &&
                            <div className={s["ContractInfo-TotalDueItem"]} onClick={togglePaymentsSection}>
                                <b>Total Due: </b>
                                <span>{data.totalDue}</span>
                            </div>
                        }
                    </div>
                </section>
                { currentUserRole == "Administrator" &&
                    paymentsSectionVisible &&
                    <section className={s["ContractInfo-PaymentsInfo"]}>
                        <div className={s["ContractInfo-Col"]}>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Payment</StyledTableCell>
                                            <StyledTableCell align="center">Date</StyledTableCell>
                                            <StyledTableCell align="center">PaidOff at</StyledTableCell>
                                            <StyledTableCell align="center">Days passed (increases in)</StyledTableCell>
                                            <StyledTableCell align="center">Interest rate applied</StyledTableCell>
                                            <StyledTableCell align="center">Attorney rate</StyledTableCell>
                                            <StyledTableCell align="center">LawFirm rate</StyledTableCell>
                                            <StyledTableCell align="center">Final rate</StyledTableCell>
                                            <StyledTableCell align="center">Amount owed</StyledTableCell>
                                            <StyledTableCell align="center">Amount paid</StyledTableCell>
                                            <StyledTableCell align="center">Amount due</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data && data.paymentsInfo?.paymentDetails && data.paymentsInfo.paymentDetails.map((item: contractPaymentDetailsType, index: number) => {
                                            let isClientPayment = item.amountSent < 0;
                                            let rowType = '';
                                            if (isClientPayment) {
                                                rowType = 'return';
                                            } else if (item.paidOffDate) {
                                                rowType = 'paid-off';
                                            }

                                            rowType = `payment-info ${rowType}`;

                                            let daysPassed = '';
                                            if (!isClientPayment) {
                                                daysPassed = item.daysBeforePriceIncrease ? `${item.daysPassed} (${item.daysBeforePriceIncrease})` : item.daysPassed.toString();
                                            }

                                            return (
                                                <StyledTableRow key={index} className={rowType}>
                                                    <StyledTableCell align="center">{currencyFormatting(item.amountSent)}</StyledTableCell>
                                                    <StyledTableCell align="center" width="75">{dateFormatting(item.paymentDate)}</StyledTableCell>
                                                    <StyledTableCell align="center" width="75">{isClientPayment || !item.paidOffDate ? '-' : dateFormatting(item.paidOffDate)}</StyledTableCell>
                                                    <StyledTableCell align="center">{daysPassed}</StyledTableCell>
                                                    <StyledTableCell align="center" width="115">
                                                        <span className={ isClientPayment ? "d-none": "font-small" }>
                                                        <strong>{item.stateInterestRateApplied.rate}%</strong> (from {item.stateInterestRateApplied.daysFrom} to {item.stateInterestRateApplied.daysTo})
                                                        </span>
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : `${item.attorneyInterestRate}%`}</StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : `${item.lawFirmInterestRate}%`}</StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : `${item.totalInterestRate}%`}</StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : currencyFormatting(item.amountToPay)}</StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : currencyFormatting(item.paidAmount)}</StyledTableCell>
                                                    <StyledTableCell align="center">{isClientPayment ? '' : currencyFormatting(item.remainingAmount)}</StyledTableCell>
                                                </StyledTableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </section>
                }
            </section>
        )
    }
    else {
        return (
            <Preloader size="100" position="center" />
        )
    }
}